import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  FileText,
  Trash2,
  CheckCircle2,
  Loader2,
  Paperclip,
  Circle,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  acceptDe,
  etiquetasDe,
  formatBytes,
  limiteDe,
  validarArchivo,
  type TipoDocumento,
} from "@/lib/formatosDocumento";

// ============================================================================
// Tipos
// ============================================================================

export type { TipoDocumento };

export type DocCapturadoEstado = "PENDIENTE" | "SUBIENDO" | "OK" | "ERROR";

/**
 * Documento seleccionado durante la captura del formulario. El archivo vive en
 * memoria (no se sube hasta que la solicitud se crea). El estado refleja el
 * progreso de subida una vez disparado el envío.
 */
export interface DocumentoCapturado {
  uiId: string;
  tipoCodigo: string;
  tipoNombre: string;
  file: File;
  esCopiaCertificada: boolean;
  descripcion: string;
  estado: DocCapturadoEstado;
  error?: string;
}

function uiId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `doc-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
  }
}

// ============================================================================
// Hooks / helpers reutilizables por las páginas de alta
// ============================================================================

/** Carga el catálogo de tipos de documento de un tag (cacheado, dedupe por react-query). */
export function useTiposDocumento(tag: string, enabled = true) {
  return useQuery<TipoDocumento[]>({
    queryKey: ["documentos-tipos", tag],
    queryFn: async () =>
      (await api.get<TipoDocumento[]>(`/api/documentos/tipos?tag=${encodeURIComponent(tag)}`)).data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

/** Tipos requeridos (requeridoTelecomBasico) que aún no están cubiertos por la captura. */
export function requeridosPendientes(
  tipos: TipoDocumento[] | undefined,
  docs: DocumentoCapturado[],
): TipoDocumento[] {
  if (!tipos) return [];
  const cubiertos = new Set(docs.map((d) => d.tipoCodigo));
  return tipos.filter((t) => t.requeridoTelecomBasico && !cubiertos.has(t.codigo));
}

/**
 * Sube secuencialmente los documentos capturados al contexto recién creado,
 * reusando el mismo endpoint que UploadDocumentoModal (POST /api/documentos).
 * Actualiza el estado de cada documento vía {@code setDocs} para reflejar
 * progreso/errores. Reintentable: omite los que ya están en OK.
 *
 * @param onUploaded callback opcional invocado con el documentoId recién creado
 *   (p.ej. para vincularlo a un expediente). Si lanza, el documento se marca
 *   como ERROR y cuenta como fallido.
 * @returns número de documentos que fallaron.
 */
export async function subirDocumentosCapturados(
  docs: DocumentoCapturado[],
  setDocs: (updater: (prev: DocumentoCapturado[]) => DocumentoCapturado[]) => void,
  contextoTipo: string,
  contextoId: string,
  onUploaded?: (documentoId: string, doc: DocumentoCapturado) => Promise<void>,
): Promise<number> {
  let fallidos = 0;
  for (const doc of docs) {
    if (doc.estado === "OK") continue;
    setDocs((prev) =>
      prev.map((d) => (d.uiId === doc.uiId ? { ...d, estado: "SUBIENDO", error: undefined } : d)),
    );
    const form = new FormData();
    form.append("archivo", doc.file);
    form.append("esCopiaCertificadaOriginal", String(doc.esCopiaCertificada));
    form.append("tipoDocumentoCodigo", doc.tipoCodigo);
    form.append("contextoTipo", contextoTipo);
    form.append("contextoId", contextoId);
    if (doc.descripcion.trim()) form.append("descripcion", doc.descripcion.trim());
    try {
      const resp = await api.post<{ documentoId: string }>("/api/documentos", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (onUploaded) await onUploaded(resp.data.documentoId, doc);
      setDocs((prev) => prev.map((d) => (d.uiId === doc.uiId ? { ...d, estado: "OK" } : d)));
    } catch (e: unknown) {
      fallidos++;
      const detail =
        (e as { response?: { data?: { detail?: string; title?: string } } }).response?.data?.detail ||
        (e as { response?: { data?: { title?: string } } }).response?.data?.title ||
        (e as Error).message ||
        "Error inesperado al subir.";
      setDocs((prev) =>
        prev.map((d) => (d.uiId === doc.uiId ? { ...d, estado: "ERROR", error: detail } : d)),
      );
    }
  }
  return fallidos;
}

// ============================================================================
// Componente de captura
// ============================================================================

/**
 * Sección de captura de documentos integrada al formulario de la solicitud
 * (requisito: los documentos forman parte de la captura, antes del envío).
 * Los archivos se mantienen en memoria y se suben tras crear la solicitud
 * mediante {@link subirDocumentosCapturados}.
 */
export function CapturaDocumentos({
  tag,
  docs,
  onChange,
  disabled,
  mostrarRequeridos,
  titulo = "Documentos",
  ayuda,
}: {
  /** Tag del catálogo de tipos (TELECOM, TELECOM_REEXP, TELECOM_AMP_PAISES, …). */
  tag: string;
  docs: DocumentoCapturado[];
  onChange: (docs: DocumentoCapturado[]) => void;
  /** Bloquea la edición (durante el envío / subida en curso). */
  disabled?: boolean;
  /** Muestra el checklist de tipos requeridos y los resalta como obligatorios. */
  mostrarRequeridos?: boolean;
  titulo?: string;
  ayuda?: string;
}) {
  const qTipos = useTiposDocumento(tag);
  const tipos = qTipos.data ?? [];
  const [tipoCodigo, setTipoCodigo] = useState("");
  const [errorLocal, setErrorLocal] = useState<string | null>(null);

  const tipoSel = tipos.find((t) => t.codigo === tipoCodigo);
  const requeridos = tipos.filter((t) => t.requeridoTelecomBasico);
  const cubiertos = new Set(docs.map((d) => d.tipoCodigo));

  const agregar = (files: FileList | null) => {
    if (!files || files.length === 0 || !tipoSel) return;
    const nuevos: DocumentoCapturado[] = [];
    for (const file of Array.from(files)) {
      const err = validarArchivo(tipoSel, file);
      if (err) {
        setErrorLocal(`${file.name}: ${err}`);
        continue;
      }
      nuevos.push({
        uiId: uiId(),
        tipoCodigo: tipoSel.codigo,
        tipoNombre: tipoSel.nombre,
        file,
        esCopiaCertificada: tipoSel.requiereCopiaCertificada,
        descripcion: "",
        estado: "PENDIENTE",
      });
    }
    if (nuevos.length) {
      setErrorLocal(null);
      onChange([...docs, ...nuevos]);
    }
  };

  const remover = (id: string) => onChange(docs.filter((d) => d.uiId !== id));
  const setCopia = (id: string, v: boolean) =>
    onChange(docs.map((d) => (d.uiId === id ? { ...d, esCopiaCertificada: v } : d)));

  return (
    <section className="rounded-lg border bg-surface p-4 shadow-card">
      <h2 className="border-b pb-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
        {titulo}
      </h2>

      {ayuda && <p className="mt-3 text-xs text-fg-subtle">{ayuda}</p>}

      {/* Checklist de requeridos */}
      {mostrarRequeridos && requeridos.length > 0 && (
        <ul className="mt-3 space-y-1 rounded border border-edge bg-surface-2/60 p-2">
          {requeridos.map((t) => {
            const ok = cubiertos.has(t.codigo);
            return (
              <li key={t.codigo} className="flex items-center gap-2 text-xs">
                {ok ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-estado-ok" />
                ) : (
                  <Circle className="h-4 w-4 shrink-0 text-fg-faint" />
                )}
                <span className={ok ? "text-fg-muted" : "font-medium text-fg"}>{t.nombre}</span>
                {!ok && <span className="text-estado-warn">· requerido</span>}
              </li>
            );
          })}
        </ul>
      )}

      {/* Selector tipo + archivo */}
      <div className="mt-3 grid items-end gap-2 sm:grid-cols-[1fr_auto]">
        <div>
          <label className="text-[11px] font-medium text-fg-muted">Tipo de documento</label>
          <select
            value={tipoCodigo}
            onChange={(e) => {
              setTipoCodigo(e.target.value);
              setErrorLocal(null);
            }}
            disabled={disabled || qTipos.isLoading}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2"
          >
            <option value="">{qTipos.isLoading ? "Cargando tipos…" : "— Selecciona —"}</option>
            {tipos.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.nombre}
                {mostrarRequeridos && t.requeridoTelecomBasico ? " ★" : ""}
              </option>
            ))}
          </select>
        </div>
        <label
          className={`inline-flex cursor-pointer items-center justify-center gap-1 rounded border border-primary-strong px-3 py-1.5 text-sm font-medium text-primary-strong transition hover:bg-primary-tint ${
            !tipoSel || disabled ? "pointer-events-none opacity-50" : ""
          }`}
        >
          <Paperclip className="h-4 w-4" />
          Adjuntar archivo
          <input
            type="file"
            multiple
            className="hidden"
            disabled={!tipoSel || disabled}
            accept={acceptDe(tipoSel)}
            onChange={(e) => {
              agregar(e.target.files);
              e.target.value = "";
            }}
          />
        </label>
      </div>
      {tipoSel && (
        <p className="mt-1 text-[11px] text-fg-subtle">
          {tipoSel.descripcion ? `${tipoSel.descripcion} · ` : ""}
          Formatos permitidos: <span className="font-medium text-fg-muted">{etiquetasDe(tipoSel)}</span> · Tamaño
          máximo: <span className="font-medium text-fg-muted">{limiteDe(tipoSel)}</span>
        </p>
      )}

      {errorLocal && (
        <div className="mt-2 flex items-start gap-2 rounded bg-estado-err-bg p-2 text-xs text-estado-err">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{errorLocal}</span>
        </div>
      )}

      {/* Lista de documentos capturados */}
      {docs.length > 0 && (
        <ul className="mt-3 space-y-1">
          {docs.map((d) => {
            const tipo = tipos.find((t) => t.codigo === d.tipoCodigo);
            return (
              <li
                key={d.uiId}
                className="rounded border border-edge bg-surface px-3 py-2 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2">
                    {d.estado === "OK" ? (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-estado-ok" />
                    ) : d.estado === "ERROR" ? (
                      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-estado-err" />
                    ) : d.estado === "SUBIENDO" ? (
                      <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-fg-faint" />
                    ) : (
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-fg-faint" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-fg">{d.tipoNombre}</div>
                      <div className="truncate text-xs text-fg-muted" title={d.file.name}>
                        {d.file.name}
                      </div>
                      <div className="text-[11px] text-fg-subtle">
                        {formatBytes(d.file.size)}
                        {d.estado === "SUBIENDO" && " · subiendo…"}
                        {d.estado === "OK" && " · subido"}
                        {d.estado === "ERROR" && d.error && (
                          <span className="text-estado-err"> · {d.error}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  {d.estado !== "OK" && d.estado !== "SUBIENDO" && !disabled && (
                    <button
                      type="button"
                      onClick={() => remover(d.uiId)}
                      className="cursor-pointer rounded p-1 text-fg-faint transition hover:bg-surface-2 hover:text-estado-err"
                      title="Quitar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                {tipo?.requiereCopiaCertificada && d.estado !== "OK" && (
                  <label className="mt-1.5 flex items-center gap-2 text-[11px] text-estado-warn">
                    <input
                      type="checkbox"
                      checked={d.esCopiaCertificada}
                      disabled={disabled}
                      onChange={(e) => setCopia(d.uiId, e.target.checked)}
                    />
                    Es copia certificada original.
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
